import { useEffect, useState } from "react";
import { CreditCard, Building2, Check } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const PROVIDERS = [
  { id: "redsys", name: "Redsys" },
  { id: "caixabank", name: "CaixaBank" },
  { id: "bbva", name: "BBVA" },
  { id: "santander", name: "Santander" },
  { id: "sabadell", name: "Banco Sabadell" },
  { id: "bankinter", name: "Bankinter" },
];

const EMPTY = { provider: "redsys", merchant_code: "", terminal: "001", secret_key: "", environment: "test", enabled: true };

export default function AdminSettings() {
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/tpv-configs").then(r => setConfigs(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/tpv-configs", form);
      toast.success(`TPV ${form.provider} guardado`);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-settings">
      <p className="overline mb-3">Configuración</p>
      <h1 className="font-heading text-4xl text-ivory mb-8">Pasarelas de pago</h1>

      {/* Stripe info */}
      <div className="bg-graphite border border-smoke p-6 mb-8">
        <div className="flex items-center gap-3 mb-2">
          <CreditCard className="text-gold" size={20} />
          <h2 className="font-heading text-xl text-ivory">Stripe</h2>
          <span className="ml-auto text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-1"><Check size={12} /> Activo</span>
        </div>
        <p className="text-ivory/60 text-sm">Pasarela principal configurada. Apple Pay, Google Pay y tarjetas se gestionan automáticamente.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-heading text-2xl text-ivory mb-6 flex items-center gap-3">
            <Building2 className="text-gold" size={22} />
            Configurar TPV bancario
          </h2>
          <p className="text-ivory/60 text-sm mb-6 leading-relaxed">
            Configure manualmente las claves de su TPV bancario. Sin necesidad de programación. Los datos se almacenan de forma segura.
          </p>

          <form onSubmit={save} className="bg-graphite border border-smoke p-6 space-y-4">
            <div>
              <label className="luxe-label">Proveedor</label>
              <select data-testid="tpv-provider" value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} className="luxe-input border-b border-smoke">
                {PROVIDERS.map(p => <option key={p.id} value={p.id} className="bg-carbon">{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="luxe-label">Código comercio</label>
              <input data-testid="tpv-merchant" required value={form.merchant_code} onChange={e => setForm({ ...form, merchant_code: e.target.value })} className="luxe-input" />
            </div>
            <div>
              <label className="luxe-label">Terminal</label>
              <input required value={form.terminal} onChange={e => setForm({ ...form, terminal: e.target.value })} className="luxe-input" />
            </div>
            <div>
              <label className="luxe-label">Clave secreta</label>
              <input data-testid="tpv-key" type="password" required value={form.secret_key} onChange={e => setForm({ ...form, secret_key: e.target.value })} className="luxe-input" />
            </div>
            <div>
              <label className="luxe-label">Entorno</label>
              <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })} className="luxe-input border-b border-smoke">
                <option value="test" className="bg-carbon">Pruebas</option>
                <option value="production" className="bg-carbon">Producción</option>
              </select>
            </div>
            <button type="submit" data-testid="save-tpv" className="btn-gold w-full">Guardar TPV</button>
          </form>
        </div>

        <div>
          <h2 className="font-heading text-2xl text-ivory mb-6">TPVs configurados</h2>
          {configs.length === 0 ? (
            <div className="bg-graphite border border-smoke p-8 text-center">
              <p className="text-ivory/40 text-sm">Aún no hay TPVs configurados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {configs.map(c => (
                <div key={c.provider} className="bg-graphite border border-smoke p-5" data-testid={`tpv-${c.provider}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-heading text-lg text-ivory uppercase">{c.provider}</p>
                      <p className="text-xs text-ivory/60 mt-1">Comercio: {c.merchant_code} · Terminal: {c.terminal}</p>
                      <p className="text-xs text-ivory/40 mt-1">Entorno: {c.environment}</p>
                    </div>
                    {c.enabled && <span className="text-[10px] uppercase tracking-[0.2em] text-gold border border-gold px-2 py-1">Activo</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 bg-graphite border border-smoke p-6">
            <h3 className="font-heading text-lg text-ivory mb-3">Otras integraciones</h3>
            <p className="overline mb-3">Próximamente</p>
            <ul className="space-y-2 text-sm text-ivory/60">
              <li>· PayPal · Bizum · Klarna</li>
              <li>· Instagram Shopping · TikTok Shop</li>
              <li>· Klaviyo · HubSpot · Mailchimp</li>
              <li>· Correos · SEUR · MRW · DHL · UPS</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
